/**
 * TanStack Query mutation hook for `POST /api/admin/finance-upload`.
 *
 * Uses XMLHttpRequest (not fetch) so the SPA can show upload progress per
 * UI-SPEC uploading-state spec (progress bar at the bottom of the drop zone).
 *
 * Multipart body shape: a single `file` part with the xlsx Buffer/Blob.
 *
 * Response handling:
 *   - 200 → resolves with FinanceUploadSuccessResponse
 *   - 422 → rejects with FinanceUploadValidationError carrying the per-cell
 *           errors array (for the UI error-state flat list)
 *   - 413 → rejects with FinanceUploadFileTooLargeError
 *   - 403 → rejects with FinanceUploadForbiddenError
 *   - 500/other → rejects with a generic FinanceUploadServerError
 *
 * onSuccess invalidations:
 *   - ['finance', 'upload-history']     — history table refreshes
 *   - ['main', 'metrics']               — Plan 05-12 ROI-tab tiles re-read
 *     (this key family is provisional; harmless if no listener exists yet)
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';

import type {
  FinanceUploadCellError,
  FinanceUploadSuccessResponse,
} from '@saledash/shared';

export class FinanceUploadValidationError extends Error {
  override name = 'FinanceUploadValidationError';
  constructor(public readonly errors: FinanceUploadCellError[]) {
    super(`Validation failed (${String(errors.length)} cell errors)`);
  }
}

export class FinanceUploadFileTooLargeError extends Error {
  override name = 'FinanceUploadFileTooLargeError';
  constructor() {
    super('File too large — max size is 1 MB.');
  }
}

export class FinanceUploadForbiddenError extends Error {
  override name = 'FinanceUploadForbiddenError';
  constructor(message = "Your account isn't in the Finance role group. Contact the CFO's office for access.") {
    super(message);
  }
}

export class FinanceUploadServerError extends Error {
  override name = 'FinanceUploadServerError';
  constructor(message = 'Server error while processing upload — try again, or contact the platform team if it persists.') {
    super(message);
  }
}

export type FinanceUploadError =
  | FinanceUploadValidationError
  | FinanceUploadFileTooLargeError
  | FinanceUploadForbiddenError
  | FinanceUploadServerError;

/**
 * Upload the xlsx file with progress reporting. Pure function — no
 * TanStack dependency — so the page can also call it directly if needed.
 */
export function uploadFinanceXlsx(
  file: File,
  onProgress?: (pct: number) => void,
): Promise<FinanceUploadSuccessResponse> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    if (onProgress) {
      xhr.upload.onprogress = (e: ProgressEvent): void => {
        if (e.lengthComputable && e.total > 0) {
          onProgress(Math.round((e.loaded / e.total) * 100));
        }
      };
    }
    xhr.onload = (): void => {
      const status = xhr.status;
      let parsed: unknown;
      try {
        parsed = xhr.responseText ? JSON.parse(xhr.responseText) : null;
      } catch {
        parsed = null;
      }
      if (status === 200 && parsed) {
        resolve(parsed as FinanceUploadSuccessResponse);
        return;
      }
      if (status === 422 && parsed && Array.isArray((parsed as { errors?: unknown[] }).errors)) {
        reject(
          new FinanceUploadValidationError(
            (parsed as { errors: FinanceUploadCellError[] }).errors,
          ),
        );
        return;
      }
      if (status === 413) {
        reject(new FinanceUploadFileTooLargeError());
        return;
      }
      if (status === 403) {
        reject(new FinanceUploadForbiddenError());
        return;
      }
      reject(new FinanceUploadServerError());
    };
    xhr.onerror = (): void => {
      reject(new FinanceUploadServerError('Network error — could not reach the server.'));
    };
    xhr.open('POST', '/api/admin/finance-upload', true);
    xhr.withCredentials = true;
    const form = new FormData();
    form.append('file', file);
    xhr.send(form);
  });
}

export interface UseFinanceUploadMutationArgs {
  onProgress?: (pct: number) => void;
}

export function useFinanceUploadMutation(args: UseFinanceUploadMutationArgs = {}) {
  const qc = useQueryClient();
  return useMutation<FinanceUploadSuccessResponse, FinanceUploadError, File>({
    mutationFn: (file: File) => uploadFinanceXlsx(file, args.onProgress),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['finance', 'upload-history'] });
      void qc.invalidateQueries({ queryKey: ['main', 'metrics'] });
    },
  });
}
